import type { WsAuthAdapter } from "./sessionTypes.js";

export interface ReadableWsAuthAdapter extends WsAuthAdapter {
  /** Transitional in-memory token for an optional WS TypeAuth frame. */
  peek(): string | null;
  clear(): void;
}

export interface InMemoryWsAuthAdapterOptions {
  readonly now?: () => number;
}

/**
 * Holds a short-lived access token in this document only. Returning sessions
 * use the verified cookie principal on the WS upgrade and therefore start with
 * `peek() === null`; this adapter never reads or writes Web Storage.
 */
export class InMemoryWsAuthAdapter implements ReadableWsAuthAdapter {
  private readonly now: () => number;
  private accessToken: string | null = null;
  private expiresAt = 0;

  constructor(options: InMemoryWsAuthAdapterOptions = {}) {
    this.now = options.now ?? Date.now;
  }

  accept(accessToken: string, expiresIn: number): void {
    if (
      accessToken === "" ||
      !Number.isFinite(expiresIn) ||
      !Number.isInteger(expiresIn) ||
      expiresIn <= 0
    ) {
      throw new Error("invalid transitional WS credential");
    }
    this.accessToken = accessToken;
    this.expiresAt = this.now() + expiresIn * 1_000;
  }

  peek(): string | null {
    if (this.accessToken === null) return null;
    if (this.now() >= this.expiresAt) {
      this.clear();
      return null;
    }
    return this.accessToken;
  }

  clear(): void {
    this.accessToken = null;
    this.expiresAt = 0;
  }
}
